import React, { useState, useCallback, useEffect, useMemo } from "react";
import TranslateManager from 'ts-translate-js';
import { RenewalPayment } from "./renewal-payment";
import { HorizontalButtonSelector } from "../../../../jsxctl/utilctl";
import { filter, findKey, forEach, setWith, isEmpty, sumBy } from 'lodash';
import { modal } from "@tsinternetsale/common";
import tsRest from 'ts-rest-client';
import { Policies } from "./policies";
import { AddressPanel } from "../address";
import { Address, AddressRequiredField, ProductDeliveryRules, ProductDeliveryShippingFee, ProductModel } from "../../../types/models/mvcapi";
import { OrderProductsDeliverySelector } from "../../molecules/buytickets/whole-cart-delivery-selector";
import { CartDeliveryStatus, DeliveryMethod, getCartDeliveryStatusFromRules, getSelectedDeliveryFromDeliveryStatus } from "../../../logic/shipping-logic";
import { UUID } from "../../../types/basetypes";
import { OrderTotalFooter, Tax, TotalKit } from "../../molecules/buytickets/order-total-footer";
import { EMPTY_UUID } from "../../../logic/util";
import { map } from "jquery";

const tr = TranslateManager.boundTR;

type RenewalActionPanelProps = {
    cid: string;
    total: number;
    eventName: string;
    eventIds: number[];
    changeRequestFormLink?: string;
    integ: string;
    clientName: string;
    clientEmail: string;
    clientPhoneNumber: string;
    reservationNumber: string;
    billingAddress: Address;
    delivRules: ProductDeliveryRules;
    products: ProductModel[];
    invoiceSubTotal: number;
    prepaidCardsTotalAmount?: number;
    transactionReceiptAmount?: number;
    totalTaxAmountByTaxName: { [key: string]: number };
    showTaxes: boolean;
    toMoney: (amount: number) => string;
    onContentChange?: () => void;
};

enum planOptionEnum {
    SUGGESTED = "SUGGESTED",
    CUSTOM = "CUSTOM",
    NO_RENEWAL = "NO_RENEWAL",
    NONE = "NONE"
}

// Delivery helper functions
const PolicyType = {
    POLICY: "POLICY",
    DISCLAIMER: "DISCLAIMER"
};

export type SelectedDeliveryMethod = {
    method: DeliveryMethod;
    shippingFeeId: UUID;
}


export function RenewalActionPanel({cid, total, eventName, eventIds, changeRequestFormLink, integ, clientName, clientEmail, clientPhoneNumber, reservationNumber, billingAddress, delivRules, products, invoiceSubTotal, totalTaxAmountByTaxName, showTaxes,  transactionReceiptAmount, onContentChange}: RenewalActionPanelProps) {
    const [showRenewalPayment, setShowRenewalPayment] = useState(false);
    const [planOption, setPlanOption] = useState(planOptionEnum.NONE);
    const [showNonRenewalAlert, setShowNonRenewalAlert] = useState(false);
    const [transactionInProgress, setTransactionInProgress] = useState(false);
    const [orderProcessingResult, setOrderProcessingResult] = useState(null);
    const [orderPrepareResult, setOrderPrepareResult] = useState(null);
    const [policiesRequest, setPoliciesRequest] = useState(null);
    const [editedBillingAddress, setEditedBillingAddress] = useState(billingAddress);
    const [addressErrors, setAddressErrors] = useState(null);
    const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<SelectedDeliveryMethod>({
        method: DeliveryMethod.NONE, 
        shippingFeeId: EMPTY_UUID,
    });

    /** Delivery status de base qu'on utilise pour passer les infos au CartDeliverySelector. */
    const [initialDelivStatus, setInitialDelivStatus] = useState<CartDeliveryStatus>(null);

    // On garde le initial shipping fee car il est inclut dans les item du panier et il doit etre soustrait a chaque calcul de total.
    const initialShippingFee = useMemo(() => initialDelivStatus ? initialDelivStatus.shippingFees.find((sf) => sf.MethodId === initialDelivStatus.mostUsedShippingFeeID) : null, [initialDelivStatus]);

    // On sort notre shipping fee sélectionné. Null si on est pas en mode mail.
    const currentShippingFee = useMemo(() => {
        if(selectedDeliveryMethod.method == DeliveryMethod.MAIL)
            return initialDelivStatus.shippingFees.find((sf) => sf.MethodId === selectedDeliveryMethod.shippingFeeId);
        return null;
    }, [selectedDeliveryMethod, initialDelivStatus]);

    console.log("totalTaxAmountByTaxName", totalTaxAmountByTaxName);
    console.log("total", total);
    const renewalTotalKit = useMemo(() => {
        //Convert totalTaxAmountByTaxName to array
        let taxes = map(totalTaxAmountByTaxName, (amount, key) => { return { name: key, amount: amount } as Tax; });

        let shippingFee = {
            Amount: 0,
            Tax1: 0,
            Tax2: 0
        } as ProductDeliveryShippingFee;
                    
        if (currentShippingFee != null) 
            shippingFee = currentShippingFee;

        let totalKit;

        // Si on affiche pas les taxes, on doit juste soustraire le shipping fee initial et ajouter le shipping fee courant.
        if(!showTaxes) {
            totalKit = {
                subTotal: total - shippingFee.Amount,
                shippingFee: shippingFee.Amount,
                taxes: [],
                total: total
            } as TotalKit;

            if(initialShippingFee != null) {
                totalKit.total = total - initialShippingFee.Amount + shippingFee.Amount;
                totalKit.subTotal = totalKit.total - shippingFee.Amount;
            }
        }
        else {
            let subTotal = invoiceSubTotal;

            let tax1 = taxes[0];
            let tax2 = taxes[1];

            // Si on a un initial shipping fee on doit le soustraire et ajouter les taxes du shipping fee courant.
            if(initialShippingFee != null) {
                subTotal = subTotal - (initialShippingFee.Amount - initialShippingFee.Tax1 - initialShippingFee.Tax2); // Amount contient déjà les taxes
                tax1.amount = tax1.amount - initialShippingFee.Tax1 + shippingFee.Tax1;
                tax2.amount = tax2.amount - initialShippingFee.Tax2 + shippingFee.Tax2;
            }
            else { // Si on a pas de shipping fee initial, on prend le shipping fee courant et on l'ajoute. 
                tax1.amount = tax1.amount + shippingFee.Tax1;
                tax2.amount = tax2.amount + shippingFee.Tax2;
            }

            totalKit = {
                subTotal: subTotal,
                shippingFee: shippingFee.Amount - shippingFee.Tax1 - shippingFee.Tax2,
                taxes: [tax1, tax2],
                total: total
            } as TotalKit;

            // on recalcul le total avec les taxes
            totalKit.total = totalKit.subTotal + totalKit.shippingFee + sumBy(totalKit.taxes, 'amount');
        }

        return totalKit;
    },[invoiceSubTotal, currentShippingFee, totalTaxAmountByTaxName, showTaxes, total, initialShippingFee]);


    // Trouver, par statistique, le meilleur shipping pour cette reservation et 
    // setter ça en default sur le sélecteur de poste.
    useEffect(() => {
        // Calculate stats
        const delivStatus = getCartDeliveryStatusFromRules(delivRules, products);
        setInitialDelivStatus(delivStatus);
        //const deliveryDetails = delivStatus.shippingFees.find((sf) => sf.MethodId === delivStatus.mostUsedShippingFeeID);
        //setDelivStatus(newDelivStatus);
        
        // Set the default shipping method
        setSelectedDeliveryMethod({
            method: getSelectedDeliveryFromDeliveryStatus(delivStatus),
            shippingFeeId: delivStatus.mostUsedShippingFeeID
        });
    }, [delivRules, products]);

    const modalContainerRef = React.useRef(null);

    const raiseContentChanged = useCallback(() => {
        // Set timeout because even though React rendered the tags, they are not yet drawn on the screen.
        setTimeout(function(){
            if(onContentChange !== undefined) onContentChange();
        }, 0);
    }, [onContentChange]);

    const checkHavePoliciesByType = useCallback((type) => {
        const policies = findKey(policiesRequest, ['type', type]);
        return (policies != null);
    }, [policiesRequest]);

    const checkAllPoliciesCheckedBeforeProcess = useCallback(()  =>{
        const notCheckedId = findKey(policiesRequest, ['checked', false]);
        return (notCheckedId == null);
    }, [policiesRequest]);

    const initPoliciesRequest = useCallback((policiesToShow) => {
        let policies = {};
        forEach(policiesToShow, (policy) => {
            // Ajoute le policiesRequest dans le dictionnaire par type
            setWith(policies, "[" + policy.id + "][checked]", false, Object);
            setWith(policies, "[" + policy.id + "][type]", policy.type, Object);
        });

        setPoliciesRequest(policies);
    }, [setPoliciesRequest]);

    const changeRenewalPlan = useCallback(() => {
        const formLink = changeRequestFormLink.replace("{clientName}", clientName).replace("{email}", clientEmail).replace("{phoneNumber}", clientPhoneNumber).replace("{reservationNumber}", reservationNumber);
        window.open(formLink, "_blank").focus();
    }, [changeRequestFormLink, clientName, clientEmail, clientPhoneNumber, reservationNumber]);

    const askForNonRenewal = () => {
        setShowNonRenewalAlert(true);
    };

    const validateBillingAddress = useCallback((address: Address) => {
        setAddressErrors(null);
        let errors = [];
        if(selectedDeliveryMethod.method == DeliveryMethod.MAIL) {
            AddressRequiredField.forEach((field) => {
                if(isEmpty(address[field.name]) || address[field.name].length < 3)
                    errors.push(field);
            });
        }
        setAddressErrors(errors);
        return errors;
    }, [selectedDeliveryMethod]);


    useEffect(() => {
        validateBillingAddress(billingAddress);
    }, [validateBillingAddress, billingAddress]);

    const handleOnPolicyCheckedChange = useCallback((id, checked) => {
        const request = {... policiesRequest};
        request[id]["checked"] = checked;

        setPoliciesRequest(request);
    }, [policiesRequest]);

    const handleOnAddressChange = useCallback((billingAddress: Address) => {
        setEditedBillingAddress(billingAddress);
        validateBillingAddress(billingAddress);
    }, [setEditedBillingAddress, validateBillingAddress]);

    const handleCompleteTransaction = useCallback(async (paymentMethod) => {
        const allPoliciesAreChecked = checkAllPoliciesCheckedBeforeProcess();

        const prepareResult = Object.assign({}, {...orderPrepareResult, 
            isSuccess: allPoliciesAreChecked && addressErrors.length === 0,
            acceptPolicies: !allPoliciesAreChecked,
            billingAddressErrors: addressErrors
         });

        if(prepareResult.isSuccess) {
            setShowNonRenewalAlert(false);
            setTransactionInProgress(true);
            try{
                const processingResult = await tsRest.post(`/renewal/reservation/${cid}/purchase`, {
                    paymentMethod,
                    sendEmail: true,
                    clientAddress: editedBillingAddress,
                    deliveryOption: selectedDeliveryMethod.method,
                    total: renewalTotalKit.total,
                    shippingFeeId: selectedDeliveryMethod.shippingFeeId
                  });

                setOrderProcessingResult(processingResult);

                if(processingResult.IsSuccess) {
                    let orderConfirmationPath = "../../";
                    if (tsapp.isLiteShop) {
                        orderConfirmationPath += tsapp.shopBasePath + "/";
                    }
                    
                    window.location.href = orderConfirmationPath + "orderconfirmation/OrderConfirmationWidget?ias=true&dse=true&cid=" + processingResult.CartID;
                }
            }
            catch(err) {
                setOrderProcessingResult(err);
                setTransactionInProgress(false);

                console.log(err);
            }
            finally {
                setTransactionInProgress(false);
            }
        }
        else {
            if (!allPoliciesAreChecked) {
                if(checkHavePoliciesByType(PolicyType.POLICY))
                    prepareResult.acceptPolicies = true;
                if(checkHavePoliciesByType(PolicyType.DISCLAIMER))
                    prepareResult.acceptDisclaimers = true;
            }
        }

        setOrderPrepareResult(prepareResult);

    }, [checkAllPoliciesCheckedBeforeProcess, orderPrepareResult, addressErrors, cid, editedBillingAddress, selectedDeliveryMethod.method, selectedDeliveryMethod.shippingFeeId, renewalTotalKit, checkHavePoliciesByType]);

    const handleplanOptionClick = useCallback((option: planOptionEnum) => {
        setPlanOption(option);

        if(option === planOptionEnum.SUGGESTED)
            setShowRenewalPayment(true);
        else
            setShowRenewalPayment(false);

        if(option === planOptionEnum.CUSTOM)
            changeRenewalPlan();

        if(option === planOptionEnum.NO_RENEWAL)
            askForNonRenewal();
    }, [changeRenewalPlan]);

    const handleOnConfirmNonRenewal = useCallback(async () => {
        setShowNonRenewalAlert(false);
        setTransactionInProgress(true);
        try{
            await tsRest.delete(`/renewal/reservation/${cid}`);
            window.location.reload();
        }
        catch(err) {
            setTransactionInProgress(false);
            console.log(err);
        }
    }, [cid]);

    const handleDeliveryMethodChange = useCallback((method, shippingFeeId) => {
        setSelectedDeliveryMethod({ method: method, shippingFeeId: shippingFeeId });
    }, []);

    // Create a delivery status that the CartDeliverySelector will understand in order to 
    // show the correct delivery options to the user and also the selected one
    const delivStatus = useMemo(() => {
        const delivStatus = Object.assign({}, initialDelivStatus, {
            itemCount: 1,
            unsetItemCount: 0,
            boxOfficeItemCount: selectedDeliveryMethod.method == DeliveryMethod.BOX_OFFICE ? 1 : 0,
            mailItemCount: selectedDeliveryMethod.method == DeliveryMethod.MAIL ? 1 : 0,
            eTicketItemCount: selectedDeliveryMethod.method == DeliveryMethod.ETICKET ? 1 : 0,
            shippingFeesCount: {},
            mostUsedShippingFeeID: selectedDeliveryMethod.shippingFeeId,
        } as CartDeliveryStatus); 

        if(selectedDeliveryMethod.method == DeliveryMethod.MAIL)
            delivStatus.shippingFeesCount[selectedDeliveryMethod.shippingFeeId] = 1;

        return delivStatus;
    }, [initialDelivStatus, selectedDeliveryMethod]);

    const actionButtonsDef = filter([
        { text: tr("Je désire renouveler"), onClick: () => handleplanOptionClick(planOptionEnum.SUGGESTED), selected: planOption === planOptionEnum.SUGGESTED },
        (changeRequestFormLink?.length > 0 ? { text: tr("Je désire faire une demande de modification"), onClick: () => handleplanOptionClick(planOptionEnum.CUSTOM), selected: planOption === planOptionEnum.CUSTOM } : null),
        { text: tr("Je ne souhaite pas renouveler"), onClick: () => handleplanOptionClick(planOptionEnum.NO_RENEWAL), selected: planOption === planOptionEnum.NO_RENEWAL  }
    ], (b) => b != null);
    
    return <div>
        <div ref={modalContainerRef}></div>
        {showNonRenewalAlert && <modal.ConfirmComponent showCloseButton={false} title={eventName}
                                    messagehtml={tr("Désirez-vous vraiment annuler votre réservation pour l'événement à venir?")}
                                    onClose={() => setShowNonRenewalAlert(false)}
                                    buttons={[
                                        { label: tr("Oui, annulez ma réservation"), onClick: handleOnConfirmNonRenewal, kind: "primary" },
                                        { label: tr("Non, conservez mes billets"), onClick: () => setShowNonRenewalAlert(false) }
                                    ]}
                                    container={modalContainerRef.current}
                                />}
        <OrderTotalFooter
            isMailDeliverySelected={selectedDeliveryMethod.method == DeliveryMethod.MAIL}
            totalKit={renewalTotalKit}
            transactionReceiptAmount={transactionReceiptAmount}
            showTaxes={showTaxes}
        />
        <HorizontalButtonSelector buttons={actionButtonsDef} className="renewal-action-button" />

        {showRenewalPayment && <>
            <OrderProductsDeliverySelector 
                delivStatus={delivStatus}
                forceDeliveryMethod={handleDeliveryMethodChange} 
                selectedShippingMethod={selectedDeliveryMethod}
            />
            <AddressPanel
                billingAddress={editedBillingAddress} 
                addressErrors={addressErrors}
                audtoEditMode={true}
                onChange={handleOnAddressChange}
            />
            <Policies eventIds={eventIds}
                integ={integ}
                raiseContentChanged={raiseContentChanged}
                onCheckedChange={handleOnPolicyCheckedChange} 
                initPoliciesRequest={initPoliciesRequest}
            />
            <RenewalPayment total={renewalTotalKit.total} 
                transactionInProgress={transactionInProgress}
                orderProcessingResult={orderProcessingResult} 
                orderPrepareResult={orderPrepareResult}
                onCompleteTransaction={handleCompleteTransaction} 
            />
        </>}
    </div>;
}
